import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, User, Mail, ShieldCheck, Lock, CreditCard, Coins, CalendarClock,
  Loader2, Check, ExternalLink, Sparkles, Crown, AlertTriangle, Ticket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { toast } from "sonner";

const NOTIF_PREFS = [
  { key: "email_notifications_enabled", label: "All email notifications", desc: "Master switch for non‑critical emails" },
  { key: "email_render_completed_enabled", label: "Render completed", desc: "When your video finishes rendering" },
  { key: "email_render_failed_enabled", label: "Render failed", desc: "If a render couldn't be completed" },
  { key: "email_billing_enabled", label: "Billing & subscription", desc: "Payments, plan changes, credit purchases" },
  { key: "email_system_enabled", label: "Account & system", desc: "Welcome, credit grants, low‑credit alerts" },
  { key: "email_marketing_enabled", label: "Product news & tips", desc: "Occasional updates (off by default)" },
];

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return null; }
};
const STATUS_STYLE = {
  active: "border-[#39FF14]/40 text-[#39FF14]",
  trialing: "border-[#00E5FF]/40 text-[#00E5FF]",
  past_due: "border-[#FFD54A]/50 text-[#FFD54A]",
  canceled: "border-white/20 text-white/50",
  free: "border-white/20 text-white/50",
  internal: "border-[#FFD54A]/60 text-[#FFD54A]",
};
const statusLabel = (s) => ({ active: "Active", trialing: "Trial", past_due: "Past due",
  canceled: "Canceled", free: "Free", internal: "Internal" }[s] || (s || "—"));

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-[#00E5FF]" />
      <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
    </div>
    {subtitle && <p className="text-white/40 text-sm mb-5">{subtitle}</p>}
    <div className={subtitle ? "" : "mt-4"}>{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-white/40 text-xs uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

export default function AccountSettings() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const owner = !!(user && user.is_owner);

  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [conf, setConf] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [sub, setSub] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const [prefs, setPrefs] = useState({});
  useEffect(() => { api.get("/account/notifications").then(({ data }) => setPrefs(data)).catch(() => {}); }, []);
  const savePref = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try {
      const { data } = await api.put("/account/notifications", { [key]: value });
      setPrefs(data);
    } catch {
      setPrefs((p) => ({ ...p, [key]: !value }));
      toast.error("Could not update preference");
    }
  };

  const redeem = async () => {
    const code = redeemCode.trim();
    if (!code) return toast.error("Enter a code");
    setRedeeming(true);
    try {
      const { data } = await api.post("/promos/redeem", { code });
      toast.success(`+${data.amount} credits! New balance: ${data.balance_after}.`);
      setRedeemCode("");
      setSub((s) => (s ? { ...s, credits: data.balance_after } : s));
      refreshUser();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not redeem code");
    } finally { setRedeeming(false); }
  };

  useEffect(() => { if (user && user.name) setName(user.name); }, [user?.name]);
  useEffect(() => {
    api.get("/account/subscription").then(({ data }) => setSub(data)).catch(() => setSub({ error: true }));
  }, []);

  const hasPassword = user ? user.has_password !== false : true;

  const saveName = async () => {
    const trimmed = (name || "").trim();
    if (trimmed.length < 2) { toast.error("Display name must be at least 2 characters."); return; }
    if (trimmed === user?.name) { toast("No changes to save."); return; }
    setSavingName(true);
    try {
      await api.patch("/account/profile", { name: trimmed });
      await refreshUser();
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update profile.");
    } finally { setSavingName(false); }
  };

  const changePassword = async () => {
    if (!cur || !nw || !conf) { toast.error("Please fill in all password fields."); return; }
    if (nw.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (nw !== conf) { toast.error("New password and confirmation do not match."); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated successfully.");
      setCur(""); setNw(""); setConf("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password.");
    } finally { setSavingPw(false); }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { return_url: window.location.href });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) ||
        "Billing management is not available yet. Please contact support or try again later.");
      setPortalLoading(false);
    }
  };

  const plan = owner ? "owner" : (sub?.plan || user?.plan || "free");
  const status = owner ? "internal" : (sub?.status || (plan === "free" ? "free" : "active"));
  const credits = owner ? "Unlimited" : (sub?.credits ?? user?.credits ?? 0);
  const interval = sub?.billing_interval;
  const renewal = fmtDate(sub?.current_period_end || sub?.plan_expires);
  const cancels = !!sub?.cancel_at_period_end;
  const billingRequired = owner ? false : !!sub?.billing_required;

  return (
    <div className="min-h-screen bg-background text-white">
      <TopNav />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/50 hover:text-white text-sm mb-6" data-testid="account-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="font-display text-3xl font-semibold tracking-tight mb-1" data-testid="account-title">Account settings</h1>
        <p className="text-white/50 text-sm mb-8">Manage your profile, security and subscription.</p>

        <div className="space-y-6">
          {/* Profile */}
          <Section icon={User} title="Profile" subtitle="Your public display name and login email." testid="account-profile">
            <div className="flex items-center gap-4 mb-5">
              {user?.picture ? (
                <img src={user.picture} alt={user?.name} referrerPolicy="no-referrer"
                  className="w-16 h-16 rounded-full object-cover border border-white/15" data-testid="account-avatar" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/30 flex items-center justify-center text-[#00E5FF] text-2xl font-semibold" data-testid="account-avatar-fallback">
                  {(user?.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="font-display text-lg font-semibold leading-tight">{user?.name}</div>
                <div className="text-white/40 text-sm">{user?.email}</div>
                {user?.picture && <div className="text-white/30 text-xs mt-0.5">Photo from your Google account</div>}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Display name">
                <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
                  className="bg-black/40 border-white/15 text-white" data-testid="account-name-input" />
              </Field>
              <Field label="Email">
                <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-white/10 bg-black/20 text-white/60 text-sm" data-testid="account-email">
                  <Mail className="w-4 h-4 text-white/30" />{user?.email || "—"}
                </div>
              </Field>
            </div>
            <div className="mt-4">
              <Button onClick={saveName} disabled={savingName}
                className="h-10 rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium" data-testid="account-save-name">
                {savingName ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save profile</>}
              </Button>
            </div>
          </Section>

          {/* Security */}
          <Section icon={ShieldCheck} title="Security" subtitle="Change the password you use to sign in." testid="account-security">
            {!hasPassword ? (
              <div className="flex items-start gap-3 rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/5 px-4 py-3" data-testid="account-social-note">
                <Lock className="w-4 h-4 text-[#00E5FF] mt-0.5" />
                <p className="text-sm text-white/70">You sign in with a social provider, so your password is managed there. Password changes aren't available here.</p>
              </div>
            ) : (
              <div className="space-y-4 max-w-md">
                <Field label="Current password">
                  <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password"
                    className="bg-black/40 border-white/15 text-white" data-testid="account-current-password" />
                </Field>
                <Field label="New password">
                  <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} autoComplete="new-password"
                    className="bg-black/40 border-white/15 text-white" data-testid="account-new-password" />
                </Field>
                <Field label="Confirm new password">
                  <Input type="password" value={conf} onChange={(e) => setConf(e.target.value)} autoComplete="new-password"
                    className="bg-black/40 border-white/15 text-white" data-testid="account-confirm-password" />
                </Field>
                <p className="text-white/30 text-xs">Use at least 8 characters.</p>
                <Button onClick={changePassword} disabled={savingPw}
                  className="h-10 rounded-full bg-[#FF007F] text-white hover:bg-[#FF007F]/90 font-medium" data-testid="account-change-password-btn">
                  {savingPw ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</> : <><Lock className="w-4 h-4 mr-2" />Update password</>}
                </Button>
              </div>
            )}
          </Section>

          {/* Subscription & Billing */}
          <Section icon={CreditCard} title="Subscription & billing"
            subtitle="Your current plan, credits and billing details." testid="account-billing">
            {!sub ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" /></div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="billing-plan">
                    <div className="text-white/40 text-xs uppercase tracking-wide flex items-center gap-1">
                      {owner ? <Crown className="w-3 h-3 text-[#FFD54A]" /> : <Sparkles className="w-3 h-3" />}Plan
                    </div>
                    <div className="font-display text-xl font-semibold mt-1 capitalize flex items-center gap-2">
                      {plan}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase ${STATUS_STYLE[status] || STATUS_STYLE.free}`} data-testid="billing-status">
                        {statusLabel(status)}
                      </span>
                    </div>
                    {!owner && interval && (
                      <div className="text-white/40 text-xs mt-1">Billed {interval === "year" ? "annually" : "monthly"}</div>
                    )}
                  </div>
                  <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="billing-credits">
                    <div className="text-white/40 text-xs uppercase tracking-wide flex items-center gap-1"><Coins className="w-3 h-3 text-[#39FF14]" />Credit balance</div>
                    <div className="font-display text-xl font-semibold mt-1">{credits}</div>
                  </div>
                </div>

                {/* Renewal / cancellation */}
                {owner ? (
                  <div className="mt-4 flex items-center gap-2 rounded-xl border border-[#FFD54A]/30 bg-[#FFD54A]/5 px-4 py-3 text-sm text-white/70" data-testid="billing-owner-note">
                    <Crown className="w-4 h-4 text-[#FFD54A]" /> Billing: <span className="text-white/90 font-medium">Not required</span> · internal owner account with unlimited access.
                  </div>
                ) : billingRequired && renewal ? (
                  cancels ? (
                    <div className="mt-4 flex items-center gap-2 rounded-xl border border-[#FFD54A]/40 bg-[#FFD54A]/5 px-4 py-3 text-sm" data-testid="billing-cancel-note">
                      <AlertTriangle className="w-4 h-4 text-[#FFD54A]" /><span className="text-white/70">Your plan is set to cancel and ends on <span className="text-white font-medium">{renewal}</span>.</span>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-center gap-2 text-sm text-white/60" data-testid="billing-renewal">
                      <CalendarClock className="w-4 h-4 text-white/40" />Renews on <span className="text-white/90 font-medium">{renewal}</span>
                    </div>
                  )
                ) : null}

                {/* Actions */}
                <div className="mt-5 flex flex-wrap gap-3">
                  {!owner && billingRequired && (
                    <Button variant="outline" onClick={openPortal} disabled={portalLoading}
                      className="h-10 rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white text-sm" data-testid="manage-billing-btn">
                      {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}Manage billing
                    </Button>
                  )}
                  {!owner && (
                    <Button onClick={() => navigate("/pricing")}
                      className="h-10 rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium text-sm" data-testid="account-upgrade-btn">
                      <Sparkles className="w-4 h-4 mr-2" />{plan === "free" ? "Upgrade plan" : plan === "studio" ? "Buy credits" : "Change plan"}
                    </Button>
                  )}
                </div>

                {/* Redeem promo code */}
                <div className="mt-5 rounded-xl border border-[#FFD54A]/25 bg-[#FFD54A]/5 p-4" data-testid="redeem-promo-card">
                  <div className="text-white/60 text-xs uppercase tracking-wide flex items-center gap-1 mb-2"><Ticket className="w-3.5 h-3.5 text-[#FFD54A]" />Have a promo code?</div>
                  <div className="flex gap-2">
                    <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="Enter code"
                      onKeyDown={(e) => e.key === "Enter" && redeem()}
                      className="bg-black/40 border-white/15 text-white font-mono" data-testid="redeem-code-input" />
                    <Button onClick={redeem} disabled={redeeming}
                      className="h-10 rounded-full bg-[#FFD54A] text-black hover:bg-[#FFD54A]/90 font-medium shrink-0" data-testid="redeem-code-btn">
                      {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Redeem"}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Section>

          <Section icon={Mail} title="Email notifications" subtitle="Choose which emails Viral3D sends you. Security & billing‑critical emails always send." testid="account-notifications">
            <div className="space-y-3" data-testid="notification-prefs">
              {NOTIF_PREFS.map((p) => (
                <label key={p.key} className="flex items-center justify-between gap-4 py-1 cursor-pointer">
                  <span>
                    <span className="text-sm text-white/85 block">{p.label}</span>
                    <span className="text-[11px] text-white/40">{p.desc}</span>
                  </span>
                  <Switch checked={!!prefs[p.key]} onCheckedChange={(v) => savePref(p.key, v)}
                    data-testid={`pref-${p.key}`} />
                </label>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
